import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useContent, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { CalendarIcon, ArrowRight, Loader2 } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { toast } from "sonner";
import { usePageSEO } from "../hooks/usePageSEO";
import axios from "axios";

export default function BookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { rooms } = useContent();
  const preselectedRoom = searchParams.get("room") || "";

  usePageSEO({
    title: "Book a Room | Ambe Palace - Best Hotel in Dungarpur, Rajasthan",
    description: "Book your stay at Ambe Palace, the best hotel in Dungarpur. Rooms from INR 849/night. Easy online booking with instant price calculation and UPI payment."
  });

  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    email: "",
    room_type: preselectedRoom,
    num_rooms: 1,
    num_persons: 2,
  });
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (preselectedRoom && !form.room_type) {
      setForm(f => ({ ...f, room_type: preselectedRoom }));
    }
  }, [preselectedRoom]);

  const selectedRoom = useMemo(() => rooms.find(r => r.id === form.room_type), [rooms, form.room_type]);

  const pricing = useMemo(() => {
    if (!selectedRoom || !checkIn || !checkOut) return null;
    const nights = differenceInDays(checkOut, checkIn);
    if (nights <= 0) return null;
    const baseCost = selectedRoom.price * nights * form.num_rooms;
    const extraPersons = Math.max(0, form.num_persons - (2 * form.num_rooms));
    const extraCharge = extraPersons * 200 * nights;
    const subtotal = baseCost + extraCharge;
    const gst = Math.round(subtotal * 0.05);
    const total = subtotal + gst;
    return { nights, baseCost, extraPersons, extraCharge, gst, total };
  }, [selectedRoom, checkIn, checkOut, form.num_rooms, form.num_persons]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.phone || !form.room_type || !checkIn || !checkOut) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!pricing || pricing.nights <= 0) {
      toast.error("Check-out must be after check-in");
      return;
    }
    setSubmitting(true);
    try {
      const bookingData = {
        ...form,
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        room_cost: pricing.baseCost,
        extra_person_charge: pricing.extraCharge,
        gst: pricing.gst,
        total_amount: pricing.total,
      };
      const res = await axios.post(`${API}/bookings`, bookingData);
      toast.success("Booking created! Proceeding to payment...");
      navigate(`/payment?id=${res.data.id}`);
    } catch (err) {
      toast.error("Failed to create booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page">
      <section className="bg-slate-900 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">Reservation</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Book Your Stay</h1>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Form */}
            <div className="lg:col-span-3 bg-white border border-stone-100 p-6 md:p-8">
              <h2 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Guest Details</h2>

              <div className="space-y-5">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Customer Name *</Label>
                  <Input
                    value={form.customer_name}
                    onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                    placeholder="Full Name"
                    className="mt-1.5 rounded-none border-stone-200 focus-visible:ring-amber-600 h-11"
                    data-testid="booking-name-input"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone Number *</Label>
                    <Input
                      value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="Phone Number"
                      className="mt-1.5 rounded-none border-stone-200 focus-visible:ring-amber-600 h-11"
                      data-testid="booking-phone-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email Address</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="Email"
                      className="mt-1.5 rounded-none border-stone-200 focus-visible:ring-amber-600 h-11"
                      data-testid="booking-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Check-in Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full mt-1.5 justify-start text-left font-normal rounded-none border-stone-200 h-11" data-testid="booking-checkin-btn">
                          <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                          {checkIn ? format(checkIn, "dd MMM yyyy") : <span className="text-slate-400">Pick date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={checkIn} onSelect={setCheckIn} disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Check-out Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full mt-1.5 justify-start text-left font-normal rounded-none border-stone-200 h-11" data-testid="booking-checkout-btn">
                          <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                          {checkOut ? format(checkOut, "dd MMM yyyy") : <span className="text-slate-400">Pick date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={checkOut} onSelect={setCheckOut} disabled={(date) => date <= (checkIn || new Date())} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Room Type *</Label>
                  {rooms.length > 0 && (
                    <Select key={`room-select-${rooms.length}`} value={form.room_type} onValueChange={v => setForm(f => ({ ...f, room_type: v }))}>
                      <SelectTrigger className="mt-1.5 rounded-none border-stone-200 h-11" data-testid="booking-room-select">
                        <SelectValue placeholder="Select room" />
                      </SelectTrigger>
                      <SelectContent>
                        {rooms.map(r => (
                          <SelectItem key={r.id} value={r.id}>{r.name} - {'\u20B9'}{r.price}/night</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Number of Rooms</Label>
                    <Input
                      type="number" min={1} max={10}
                      value={form.num_rooms}
                      onChange={e => setForm(f => ({ ...f, num_rooms: parseInt(e.target.value) || 1 }))}
                      className="mt-1.5 rounded-none border-stone-200 focus-visible:ring-amber-600 h-11"
                      data-testid="booking-rooms-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Number of Persons</Label>
                    <Input
                      type="number" min={1} max={20}
                      value={form.num_persons}
                      onChange={e => setForm(f => ({ ...f, num_persons: parseInt(e.target.value) || 1 }))}
                      className="mt-1.5 rounded-none border-stone-200 focus-visible:ring-amber-600 h-11"
                      data-testid="booking-persons-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Price Summary */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-stone-100 p-6 sticky top-24">
                <h2 className="text-lg font-bold text-slate-900 mb-5" style={{ fontFamily: 'Playfair Display, serif' }}>Price Summary</h2>
                {pricing ? (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Room ({selectedRoom?.name})</span>
                      <span className="text-slate-700 font-semibold">{'\u20B9'}{selectedRoom?.price} x {pricing.nights}N x {form.num_rooms}R</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Room Cost</span>
                      <span className="text-slate-800 font-bold">{'\u20B9'}{pricing.baseCost.toLocaleString()}</span>
                    </div>
                    {pricing.extraCharge > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Extra Person ({pricing.extraPersons} x {'\u20B9'}200 x {pricing.nights}N)</span>
                        <span className="text-slate-800 font-bold">{'\u20B9'}{pricing.extraCharge.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">GST (5%)</span>
                      <span className="text-slate-800 font-bold">{'\u20B9'}{pricing.gst.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-stone-200 pt-3 mt-3">
                      <div className="flex justify-between">
                        <span className="font-bold text-slate-900">Total Amount</span>
                        <span className="text-xl font-bold text-amber-600">{'\u20B9'}{pricing.total.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Select room type and dates to see pricing</p>
                )}

                <Button
                  type="submit"
                  disabled={!pricing || submitting}
                  className="w-full mt-6 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-none h-12 disabled:opacity-50 transition-colors duration-200"
                  data-testid="continue-to-payment-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Continue to Payment <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
